import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { compute, FIELDS, STRESS } from "@/lib/finance";
import { usd, usd2, pct, pctFrac, mult, num } from "@/lib/format";

const GREEN = [15, 46, 35];
const GOLD = [197, 155, 39];
const CREAM = [249, 246, 240];
const clean = (s) => String(s ?? "").replace(/[\u2013\u2014]/g, "-").replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/\u00d7/g, "x").replace(/[^\x20-\x7E]/g, "");
const bps = (v) => (v === null || v === undefined ? "n/a" : `${(v * 10000).toFixed(0)} bps`);
const fieldVal = (f, v) => (f.type === "currency" ? usd(v) : f.type === "pct" ? pct(v) : num(v));

function results(r) {
  return [
    ["Total Project Cost", usd(r.totalCost), "Levered IRR", pctFrac(r.leveredIRR)],
    ["Cost per Key", usd(r.costPerKey), "Unlevered IRR", pctFrac(r.unleveredIRR)],
    ["Loan / Equity", `${usd(r.loan)} / ${usd(r.equity)}`, "Equity Multiple", mult(r.equityMultiple)],
    ["Year 1 RevPAR", usd2(r.revpar1), "Cash-on-Cash (Y1 / avg)", `${pctFrac(r.coc1)} / ${pctFrac(r.avgCoc)}`],
    ["Year 1 NOI", usd(r.noi1), "DSCR (Y1 / min)", `${r.dscr1 ? r.dscr1.toFixed(2) + "x" : "n/a"} / ${r.minDscr ? r.minDscr.toFixed(2) + "x" : "n/a"}`],
    ["Stabilized NOI (Y3)", usd(r.stabNoi), "Yield on Cost / Spread", `${pctFrac(r.yieldOnCost)} / ${bps(r.devSpread)}`],
    ["Projected Exit Value", usd(r.exitValue), "Exit Value per Key", usd(r.exitPerKey)],
  ];
}

function assumptions(inputs) {
  const cells = FIELDS.map((f) => [f.label, fieldVal(f, inputs[f.key])]);
  const rows = [];
  const per = Math.ceil(cells.length / 3);
  for (let k = 0; k < per; k++) rows.push([...(cells[k] || ["", ""]), ...(cells[k + per] || ["", ""]), ...(cells[k + 2 * per] || ["", ""])]);
  return rows;
}

function stressRows(baseInputs, baseLabel) {
  const r = compute(baseInputs);
  const cases = STRESS.map((s) => compute(s.apply(baseInputs)));
  const line = (label, fn) => [label, fn(r), ...cases.map(fn)];
  return {
    head: [[{ content: "Metric", styles: { halign: "left" } }, baseLabel, ...STRESS.map((s) => s.label)]],
    body: [
      line("Total Project Cost", (x) => usd(x.totalCost)),
      line("Yield on Cost", (x) => pctFrac(x.yieldOnCost)),
      line("Development Spread", (x) => bps(x.devSpread)),
      line("Exit Value", (x) => usd(x.exitValue)),
      line("Levered IRR", (x) => pctFrac(x.leveredIRR)),
      line("Equity Multiple", (x) => mult(x.equityMultiple)),
      ["Change in Levered IRR", "-", ...cases.map((x) => (x.leveredIRR === null || r.leveredIRR === null ? "n/a" : bps(x.leveredIRR - r.leveredIRR)))],
    ],
  };
}

export function buildSummaryPdf({ content, inputs, scenario, stressBase }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 36;
  const r = compute(inputs);
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  doc.setFillColor(...GREEN).rect(0, 0, W, 74, "F");
  doc.setFillColor(...GOLD).rect(0, 74, W, 2, "F");
  doc.setTextColor(...CREAM).setFont("times", "bold").setFontSize(17).text(clean(content.project_name), M, 32);
  doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(220, 214, 200).text(clean(content.address), M, 48, { maxWidth: W * 0.58 });
  doc.setTextColor(...GOLD).setFont("helvetica", "bold").setFontSize(8).text("SCENARIO SUMMARY", W - M, 28, { align: "right" });
  doc.setTextColor(...CREAM).setFontSize(10).text(clean(scenario), W - M, 42, { align: "right", maxWidth: W * 0.36 });
  doc.setFont("helvetica", "normal").setFontSize(7.5).text(`Generated ${date}`, W - M, 60, { align: "right" });

  doc.setTextColor(150, 30, 30).setFont("helvetica", "bold").setFontSize(7.5)
    .text("HYPOTHETICAL PROJECTIONS - NOT GUARANTEED. See disclaimer below.", M, 92);

  const section = (title, y) => {
    doc.setTextColor(...GREEN).setFont("times", "bold").setFontSize(11.5).text(title, M, y);
    return y + 5;
  };
  const base = { margin: { left: M, right: M }, theme: "grid", styles: { fontSize: 7.5, cellPadding: 3, lineColor: [226, 220, 210], lineWidth: 0.4, textColor: [26, 29, 26] }, headStyles: { fillColor: GREEN, textColor: CREAM, fontStyle: "bold" } };

  autoTable(doc, { ...base, startY: section("Key Results", 110), body: results(r).map((row) => row.map(clean)),
    columnStyles: { 0: { fontStyle: "bold", fillColor: CREAM }, 1: { halign: "right" }, 2: { fontStyle: "bold", fillColor: CREAM }, 3: { halign: "right" } } });

  autoTable(doc, { ...base, startY: section("Scenario Assumptions", doc.lastAutoTable.finalY + 18), body: assumptions(inputs).map((row) => row.map(clean)),
    styles: { ...base.styles, fontSize: 6.8, cellPadding: 2.4 },
    columnStyles: { 1: { halign: "right", fontStyle: "bold" }, 3: { halign: "right", fontStyle: "bold" }, 5: { halign: "right", fontStyle: "bold" } } });

  const sb = stressBase || { inputs, label: "Scenario" };
  const st = stressRows(sb.inputs, sb.label);
  autoTable(doc, { ...base, startY: section(`Stress Tests (applied to: ${clean(sb.label)})`, doc.lastAutoTable.finalY + 18), head: st.head.map((row) => row.map((c) => (typeof c === "string" ? clean(c) : c))), body: st.body.map((row) => row.map(clean)),
    styles: { ...base.styles, fontSize: 7, halign: "right" }, columnStyles: { 0: { halign: "left", fontStyle: "bold", fillColor: CREAM } } });

  let y = doc.lastAutoTable.finalY + 10;
  doc.setTextColor(90, 90, 90).setFont("helvetica", "normal").setFontSize(6.3);
  doc.text(clean("Stress cases: construction cost +10% (hard, soft, FF&E x 1.10); exit cap +50 bps; slower ramp-up (Y1 -10 pts, Y2 -5 pts occupancy); combined applies all three. Methodology: NOI = revenue - operating expenses, management fee, FF&E reserve, taxes and insurance; exit value = forward NOI / exit cap; IRR solved on annual cash flows."), M, y, { maxWidth: W - 2 * M });

  const disc = doc.splitTextToSize(clean(content.disclaimer), W - 2 * M);
  const dy = H - 40 - disc.length * 7.4;
  doc.setDrawColor(...GOLD).setLineWidth(0.6).line(M, dy - 8, W - M, dy - 8);
  doc.setFontSize(6.2).setTextColor(70, 70, 70).text(disc, M, dy);
  doc.setFontSize(7).setTextColor(...GREEN).setFont("helvetica", "bold").text("Confidential - NOLA Project", M, H - 22);
  doc.setFont("helvetica", "normal").text("Not an offer to sell or a solicitation of an offer to buy any security.", W - M, H - 22, { align: "right" });

  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.06 }));
  doc.setTextColor(...GREEN).setFont("times", "bold").setFontSize(54).text("CONFIDENTIAL", W / 2, H / 2 + 60, { align: "center", angle: 30 });
  doc.restoreGraphicsState();
  return doc;
}

export function downloadSummaryPdf(opts) {
  const slug = clean(opts.scenario).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").slice(0, 40) || "scenario";
  buildSummaryPdf(opts).save(`Tempo-NOLA-Summary-${slug}.pdf`);
}
